import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  DateTimePicker,
  Field,
  FastForwardIcon,
  Input,
  ReplayIcon,
  Stack,
  Text,
  AccessTimeIcon,
  WarningIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@billda/design-system';
import {
  useGetCurrentTime,
  useSetTime,
  useAdvanceTime,
  useResetTime,
} from '../../../features/development/hooks/useTimeManipulation';
import { formatDistanceToNow } from 'date-fns';

export const TimeDebugSection: React.FC = () => {
  // State
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [customUnits, setCustomUnits] = useState({
    years: 0,
    months: 0,
    weeks: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Queries and mutations
  const {
    data: timeData,
    isLoading,
    refetch,
  } = useGetCurrentTime({ autoRefresh });
  const setTimeMutation = useSetTime();
  const advanceTimeMutation = useAdvanceTime();
  const resetTimeMutation = useResetTime();

  // Handlers
  const handleManualRefresh = () => {
    refetch();
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleSetTime = () => {
    if (!selectedDateTime) {
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Set Time',
      message: `Set server time to ${selectedDateTime.toLocaleString()}?`,
      onConfirm: () => {
        setTimeMutation.mutate(selectedDateTime);
        setConfirmDialog(null);
      },
    });
  };

  const handleQuickAdvance = (units: {
    hours?: number;
    days?: number;
    weeks?: number;
  }) => {
    const unitLabels: string[] = [];
    if (units.hours) {
      unitLabels.push(`${units.hours} hour(s)`);
    }
    if (units.days) {
      unitLabels.push(`${units.days} day(s)`);
    }
    if (units.weeks) {
      unitLabels.push(`${units.weeks} week(s)`);
    }

    setConfirmDialog({
      open: true,
      title: 'Advance Time',
      message: `Advance time by ${unitLabels.join(', ')}?`,
      onConfirm: () => {
        advanceTimeMutation.mutate(units);
        setConfirmDialog(null);
      },
    });
  };

  const handleCustomAdvance = () => {
    const hasAnyValue = Object.values(customUnits).some(v => v !== 0);
    if (!hasAnyValue) {
      return;
    }

    const unitLabels = Object.entries(customUnits)
      .filter(([, value]) => value !== 0)
      .map(([key, value]) => `${value} ${key}`);

    setConfirmDialog({
      open: true,
      title: 'Advance Time',
      message: `Advance time by ${unitLabels.join(', ')}?`,
      onConfirm: () => {
        advanceTimeMutation.mutate(customUnits);
        setConfirmDialog(null);
        // Reset form
        setCustomUnits({
          years: 0,
          months: 0,
          weeks: 0,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
      },
    });
  };

  const handleReset = () => {
    setConfirmDialog({
      open: true,
      title: 'Reset Time',
      message: 'Reset to system time? This will clear any time manipulation.',
      onConfirm: () => {
        resetTimeMutation.mutate();
        setConfirmDialog(null);
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(null);
  };

  // Format current time
  const currentTime = timeData?.currentTime
    ? new Date(timeData.currentTime)
    : null;
  const systemTime = timeData?.systemTime
    ? new Date(timeData.systemTime)
    : null;
  const isManipulated = timeData?.isManipulated ?? false;

  const unitFields = [
    { key: 'years', label: 'Years' },
    { key: 'months', label: 'Months' },
    { key: 'weeks', label: 'Weeks' },
    { key: 'days', label: 'Days' },
    { key: 'hours', label: 'Hours' },
    { key: 'minutes', label: 'Minutes' },
  ] as const;

  return (
    <Stack spacing={3}>
      {/* Warning Banner */}
      {isManipulated && (
        <Alert severity="warning" icon={<WarningIcon aria-hidden="true" />}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <Text variant="body2" className="font-semibold">
                Time is currently manipulated
              </Text>
              <Text variant="caption">
                Server time is not reflecting real time. Click Reset to return
                to system time.
              </Text>
            </div>
            <Button variant="ghost" size="sm" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </Alert>
      )}

      {/* Current Time Display */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AccessTimeIcon
                aria-hidden="true"
                className="size-5 text-content-accent"
              />
              <Text as="h6" variant="h6">
                Current Server Time
              </Text>
              {isManipulated && (
                <Chip
                  label="MANIPULATED"
                  color="warning"
                  size="sm"
                  icon={<WarningIcon aria-hidden="true" />}
                />
              )}
            </div>
            <div className="flex gap-2">
              <Chip
                label={autoRefresh ? 'Auto' : 'Manual'}
                size="sm"
                color={autoRefresh ? 'success' : 'default'}
                onClick={handleToggleAutoRefresh}
                className="cursor-pointer"
                title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              />
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={handleManualRefresh}
                aria-label="Refresh now"
                title="Refresh now"
              >
                <ReplayIcon aria-hidden="true" className="size-4" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <Text variant="body2" color="secondary">
              Loading...
            </Text>
          ) : currentTime ? (
            <Stack spacing={1}>
              <Text as="h4" variant="h4" className="font-bold">
                {currentTime.toLocaleString('en-GB', {
                  dateStyle: 'full',
                  timeStyle: 'long',
                })}
              </Text>
              <Text variant="body2" color="secondary">
                {formatDistanceToNow(currentTime, { addSuffix: true })}
              </Text>
              {isManipulated && systemTime && (
                <Text variant="caption" className="text-content-warning">
                  System time: {systemTime.toLocaleString()} (offset:{' '}
                  {formatDistanceToNow(systemTime, {
                    addSuffix: false,
                    includeSeconds: true,
                  })}
                  )
                </Text>
              )}
            </Stack>
          ) : (
            <Text variant="body2" className="text-content-error">
              Failed to load time
            </Text>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-6">
          <Text as="h6" variant="h6" className="mb-2">
            Quick Actions
          </Text>
          <Text variant="body2" color="secondary" className="mb-4">
            Common time adjustments for testing
          </Text>
          <div className="flex flex-wrap gap-4">
            {(
              [
                { label: '+1 Hour', units: { hours: 1 } },
                { label: '+1 Day', units: { days: 1 } },
                { label: '+1 Week', units: { weeks: 1 } },
                { label: '+30 Days', units: { days: 30 } },
                { label: '+90 Days', units: { days: 90 } },
              ] as const
            ).map(action => (
              <div
                key={action.label}
                className="min-w-[150px] flex-[1_1_calc(33.333%-16px)]"
              >
                <Button
                  fullWidth
                  variant="outline"
                  onClick={() => handleQuickAdvance(action.units)}
                >
                  <FastForwardIcon aria-hidden="true" className="size-4" />
                  {action.label}
                </Button>
              </div>
            ))}
            <div className="min-w-[150px] flex-[1_1_calc(33.333%-16px)]">
              <Button fullWidth variant="danger" onClick={handleReset}>
                <ReplayIcon aria-hidden="true" className="size-4" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Set Specific Time */}
      <Accordion>
        <AccordionItem value="set-time" title="Set Specific Time">
          <Stack spacing={2}>
            <Text variant="body2" color="secondary">
              Set server time to a specific date and time
            </Text>
            <DateTimePicker
              label="Select Date & Time"
              value={selectedDateTime}
              onChange={setSelectedDateTime}
            />
            <Button
              variant="neutral"
              onClick={handleSetTime}
              disabled={!selectedDateTime || setTimeMutation.isPending}
            >
              Set Time
            </Button>
          </Stack>
        </AccordionItem>
      </Accordion>

      {/* Advanced Controls */}
      <Accordion>
        <AccordionItem value="advanced" title="Advanced Time Control">
          <Stack spacing={2}>
            <Text variant="body2" color="secondary">
              Advance time by multiple units simultaneously
            </Text>
            <div className="flex flex-wrap gap-4">
              {unitFields.map(unit => (
                <div
                  key={unit.key}
                  className="min-w-[150px] flex-[1_1_calc(33.333%-16px)]"
                >
                  <Field label={unit.label}>
                    <Input
                      type="number"
                      value={customUnits[unit.key]}
                      onChange={e =>
                        setCustomUnits({
                          ...customUnits,
                          [unit.key]: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </Field>
                </div>
              ))}
            </div>
            <Button
              variant="neutral"
              onClick={handleCustomAdvance}
              disabled={advanceTimeMutation.isPending}
            >
              Advance Time
            </Button>
          </Stack>
        </AccordionItem>
      </Accordion>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog
          open={confirmDialog.open}
          onOpenChange={next => {
            if (!next) {
              closeConfirmDialog();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>{confirmDialog.title}</DialogHeader>
            <DialogBody>{confirmDialog.message}</DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={closeConfirmDialog}>
                Cancel
              </Button>
              <Button variant="neutral" onClick={confirmDialog.onConfirm}>
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Stack>
  );
};
